import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, Check, CheckCheck, Globe2 } from "lucide-react";

const POLL_INTERVAL_MS = 60000; // 60s

const ICON_FOR = {
  domain_verified: Globe2,
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Bell icon + dropdown that shows the workspace's recent notifications.
 * - Polls /api/notifications every 60s while mounted.
 * - Bell shows an unread count badge (99+ cap).
 * - Click item -> mark-read + navigate (if `url` is set).
 * - "Mark all read" clears everything.
 * Rendered inside DashboardLayout so it appears on every internal page.
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const skip = !user || user.impersonating;

  const load = async () => {
    if (skip) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (skip) { setItems([]); setUnread(0); return; }
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, user?.email]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch {}
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.url) { setOpen(false); nav(n.url); }
  };

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  if (skip) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-lg hover:bg-[#F1F3F7] text-[#4A4D57] hover:text-[#0B0D17]"
        aria-label="Notifications"
        data-testid="notif-bell"
      >
        <Bell className="w-4 h-4" strokeWidth={1.75} />
        {unread > 0 && (
          <span
            data-testid="notif-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#B00020] text-white text-[9px] font-bold flex items-center justify-center leading-none"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-[320px] sm:w-[360px] vp-glass rounded-xl shadow-xl border border-[#E4E6EC] overflow-hidden z-50"
        >
          <div className="px-4 py-2.5 flex items-center justify-between border-b border-[#E4E6EC]">
            <div className="font-display font-semibold text-sm">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#0B5CFF] hover:underline inline-flex items-center gap-1"
                data-testid="notif-mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-xs text-[#747680]">You're all caught up 🎉</div>
            ) : (
              items.map((n) => {
                const Icon = ICON_FOR[n.kind] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => openItem(n)}
                    data-testid={`notif-item-${n.id}`}
                    className={"w-full text-left px-4 py-3 flex items-start gap-3 border-b border-[#E4E6EC] last:border-b-0 hover:bg-[#F1F3F7] " + (n.read ? "opacity-70" : "")}
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#0B5CFF]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[#0B0D17] truncate">{n.title}</div>
                      {n.body && <div className="text-xs text-[#4A4D57] mt-0.5 line-clamp-2">{n.body}</div>}
                      <div className="text-[10px] text-[#747680] mt-1">{timeAgo(n.created_at)}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#0B5CFF] flex-shrink-0 mt-1.5" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
